import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Save, FileText } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("brgt_token");
const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

export default function AdminPages() {
  const [pages, setPages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editSections, setEditSections] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/pages`).then(r => setPages(r.data)).catch(() => {});
  }, []);

  const selectPage = (page) => {
    setSelected(page);
    setEditSections(JSON.parse(JSON.stringify(page.sections || [])));
  };

  const updateSectionValue = (idx, value) => {
    const updated = [...editSections];
    updated[idx].value = value;
    setEditSections(updated);
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await axios.put(`${API}/pages/${selected.slug}`, { sections: editSections }, authHeaders());
      toast.success("Page saved!");
      const res = await axios.get(`${API}/pages`);
      setPages(res.data);
    } catch {
      toast.error("Failed to save");
    }
    setSaving(false);
  };

  return (
    <div data-testid="admin-pages">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold text-navy" data-testid="pages-title">Page Management</h1>
        <p className="text-slate-500 text-sm mt-1">Edit content for each page on your website</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Page List */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
            <div className="p-3 border-b border-slate-100">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Pages</h3>
            </div>
            <div className="p-2 space-y-1">
              {pages.map((page) => (
                <button
                  key={page.slug}
                  onClick={() => selectPage(page)}
                  data-testid={`page-item-${page.slug}`}
                  className={`flex items-center gap-2 w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    selected?.slug === page.slug
                      ? "bg-navy text-white"
                      : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <FileText size={14} />
                  {page.title}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Editor */}
        <div className="lg:col-span-9">
          {selected ? (
            <div className="bg-white rounded-xl border border-slate-100 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-heading font-semibold text-navy text-lg">{selected.title}</h2>
                  <p className="text-xs text-slate-400 mt-1">Slug: /{selected.slug}</p>
                </div>
                <button
                  onClick={handleSave}
                  data-testid="save-page-btn"
                  disabled={saving}
                  className="inline-flex items-center gap-2 bg-navy text-white hover:bg-navy-light rounded-full px-5 py-2 text-sm font-semibold transition-all disabled:opacity-50"
                >
                  <Save size={14} /> {saving ? "Saving..." : "Save"}
                </button>
              </div>

              <div className="space-y-5">
                {editSections.map((section, idx) => (
                  <div key={section.key} data-testid={`section-editor-${section.key}`}>
                    <label className="block text-sm font-medium text-navy mb-1.5">
                      {section.label || section.key}
                    </label>
                    {section.value && section.value.length > 100 ? (
                      <textarea
                        value={section.value}
                        onChange={e => updateSectionValue(idx, e.target.value)}
                        rows={4}
                        className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-navy/20 focus:border-navy outline-none transition-all resize-none text-sm"
                      />
                    ) : (
                      <input
                        type="text"
                        value={section.value}
                        onChange={e => updateSectionValue(idx, e.target.value)}
                        className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-navy/20 focus:border-navy outline-none transition-all text-sm"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-100 p-12 text-center">
              <FileText size={48} className="text-slate-200 mx-auto mb-4" />
              <p className="text-slate-400">Select a page to edit its content</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
